/**
 * Conversation sidebar.
 *
 * The old screen kept a single conversation in component state, so a refresh —
 * or clicking one of its own navigation cards — silently destroyed it. Threads
 * now persist, and this is how they are reached: search, date-grouped list,
 * rename, delete.
 *
 * On phones this is an off-canvas drawer over the thread; the parent owns the
 * open flag and the backdrop.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../../i18n';
import { useSwal } from '../../../components/Notifications/swal/SwalCom';
import useAssistantStore from '../store/useAssistantStore';
import { groupThreadsByDate } from '../utils/threadStorage';

const GROUP_LABEL_KEYS = {
  today: 'assistant.sidebar.today',
  yesterday: 'assistant.sidebar.yesterday',
  week: 'assistant.sidebar.week',
  older: 'assistant.sidebar.older',
};

const AssistantSidebar = ({ threads, activeThreadId, onNewChat }) => {
  const { t } = useI18n();
  const { swalConfirm } = useSwal();

  const search = useAssistantStore((state) => state.search);
  const setSearch = useAssistantStore((state) => state.setSearch);
  const selectThread = useAssistantStore((state) => state.selectThread);
  const renameThread = useAssistantStore((state) => state.renameThread);
  const deleteThread = useAssistantStore((state) => state.deleteThread);
  const deleteAllThreads = useAssistantStore((state) => state.deleteAllThreads);
  const closeSidebar = useAssistantStore((state) => state.closeSidebar);

  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState('');
  const renameRef = useRef(null);

  /** Search matches the title and the message text, so an old answer is findable. */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return threads;
    return threads.filter((thread) => {
      if ((thread.title || '').toLowerCase().includes(term)) return true;
      return (thread.messages || []).some((message) =>
        (message.content || '').toLowerCase().includes(term),
      );
    });
  }, [search, threads]);

  const groups = useMemo(() => groupThreadsByDate(filtered), [filtered]);

  const startRename = useCallback((thread) => {
    setRenamingId(thread.id);
    setRenameText(thread.title || '');
    // Focus after the input has actually mounted.
    window.setTimeout(() => renameRef.current?.select(), 0);
  }, []);

  const commitRename = useCallback(() => {
    if (renamingId) renameThread(renamingId, renameText);
    setRenamingId(null);
    setRenameText('');
  }, [renameText, renameThread, renamingId]);

  const confirmDelete = useCallback(
    (thread) => {
      swalConfirm(
        t('assistant.sidebar.deleteTitle'),
        t('assistant.sidebar.deleteBody', { title: thread.title || t('assistant.sidebar.untitled') }),
        (result) => {
          if (result?.isConfirmed) deleteThread(thread.id);
        },
      );
    },
    [deleteThread, swalConfirm, t],
  );

  const confirmDeleteAll = useCallback(() => {
    swalConfirm(t('assistant.sidebar.clearTitle'), t('assistant.sidebar.clearBody'), (result) => {
      if (result?.isConfirmed) deleteAllThreads();
    });
  }, [deleteAllThreads, swalConfirm, t]);

  const renderThread = (thread) => {
    const isActive = thread.id === activeThreadId;
    const isRenaming = thread.id === renamingId;
    const preview =
      (thread.messages || []).length === 0
        ? t('assistant.sidebar.empty')
        : t('assistant.sidebar.count', { count: thread.messages.length });

    if (isRenaming) {
      return (
        <li key={thread.id} className="lf-thread-row lf-thread-row--renaming">
          <input
            ref={renameRef}
            className="lf-thread-row__rename"
            value={renameText}
            onChange={(event) => setRenameText(event.target.value)}
            onBlur={commitRename}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitRename();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setRenamingId(null);
              }
            }}
            aria-label={t('assistant.sidebar.rename')}
          />
        </li>
      );
    }

    return (
      <li key={thread.id} className={`lf-thread-row${isActive ? ' lf-thread-row--active' : ''}`}>
        <button
          type="button"
          className="lf-thread-row__main"
          onClick={() => selectThread(thread.id)}
          aria-current={isActive ? 'true' : undefined}
        >
          <i className="fa fa-comment-alt lf-thread-row__icon" aria-hidden="true" />
          <span className="lf-thread-row__text">
            <span className="lf-thread-row__title">
              {thread.title || t('assistant.sidebar.untitled')}
            </span>
            <span className="lf-thread-row__meta">{preview}</span>
          </span>
        </button>
        <span className="lf-thread-row__tools">
          <button
            type="button"
            className="lf-thread-row__tool"
            onClick={() => startRename(thread)}
            title={t('assistant.sidebar.rename')}
            aria-label={t('assistant.sidebar.rename')}
          >
            <i className="fa fa-pen" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="lf-thread-row__tool lf-thread-row__tool--danger"
            onClick={() => confirmDelete(thread)}
            title={t('assistant.sidebar.delete')}
            aria-label={t('assistant.sidebar.delete')}
          >
            <i className="fa fa-trash-alt" aria-hidden="true" />
          </button>
        </span>
      </li>
    );
  };

  return (
    <aside className="lf-sidebar" aria-label={t('assistant.sidebar.heading')}>
      <div className="lf-sidebar__top">
        <button type="button" className="lf-sidebar__new" onClick={onNewChat}>
          <i className="fa fa-plus" aria-hidden="true" />
          {t('assistant.sidebar.newChat')}
        </button>
        <button
          type="button"
          className="lf-sidebar__close lf-only-md"
          onClick={closeSidebar}
          title={t('common.close')}
          aria-label={t('common.close')}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </div>

      <div className="lf-sidebar__search">
        <i className="fa fa-search" aria-hidden="true" />
        {/*
          `lf-field-bare` is load-bearing, not decoration. Without it the global
          `input:focus` rules in theme-overrides.css (all `!important`) paint a
          second border and a second 3px ring inside this wrapper's own — the
          doubled highlight. The wrapper owns the chrome via `:focus-within`.
        */}
        <input
          type="search"
          className="lf-field-bare"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('assistant.sidebar.searchPlaceholder')}
          aria-label={t('assistant.sidebar.searchPlaceholder')}
        />
        {!!search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            title={t('common.remove')}
            aria-label={t('common.remove')}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="lf-sidebar__list">
        {filtered.length === 0 ? (
          <p className="lf-sidebar__none">
            {search ? t('assistant.sidebar.noMatches') : t('assistant.sidebar.noThreads')}
          </p>
        ) : (
          Object.entries(GROUP_LABEL_KEYS).map(([key, labelKey]) =>
            groups[key].length ? (
              <section key={key} className="lf-sidebar__group">
                <h2 className="lf-sidebar__group-title">{t(labelKey)}</h2>
                <ul className="lf-sidebar__threads">{groups[key].map(renderThread)}</ul>
              </section>
            ) : null,
          )
        )}
      </div>

      {threads.length > 0 && (
        <div className="lf-sidebar__foot">
          <button type="button" className="lf-sidebar__clear" onClick={confirmDeleteAll}>
            <i className="fa fa-trash-alt" aria-hidden="true" />
            {t('assistant.sidebar.clearAll')}
          </button>
        </div>
      )}
    </aside>
  );
};

export default AssistantSidebar;
